import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { Portfolio, ReviewStatus } from '@ndvr/portfolio-api';

import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import {
    formatDate,
    PORTFOLIO_TYPE_DISPLAY_MAP,
    PORTFOLIO_TYPE_TAG_MAP,
    useHouseholdRoutePaths,
    useRequiredParams,
} from 'utils';
import { usePortfolios } from 'views/portfolio/portfolio-queries';
import { PortfolioReviewStatus } from 'views/portfolio/PortfolioReviewStatus';
import { CondensedTable, PageSpinner, Tag, Text, TextLink, Title, VerticalLayout } from 'widgets';

const columnHelper = createColumnHelper<Portfolio>();

export const OutstandingPortfoliosList = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const { data: portfolios, isPending: portfoliosLoading } = usePortfolios(globalAccountGUID);

    const filteredPortfolios = useMemo(() => {
        return (portfolios || []).reduce<Portfolio[]>((result, portfolio) => {
            if (portfolio.originPortfolioGUID) {
                const original = portfolios?.find(
                    ({ portfolioGUID }) => portfolioGUID === portfolio.originPortfolioGUID,
                );
                return original ? [...result, original] : result;
            } else if (portfolio.reviewStatus === ReviewStatus.READY_FOR_REVIEW) {
                return [...result, portfolio];
            }
            return result;
        }, []);
    }, [portfolios]);

    const columns = [
        columnHelper.accessor('portfolioType', {
            id: 'type',
            header: 'Type',
            cell: ({ getValue }) => (
                <Tag type={PORTFOLIO_TYPE_TAG_MAP[getValue()]}>{PORTFOLIO_TYPE_DISPLAY_MAP[getValue()]}</Tag>
            ),
        }),
        columnHelper.accessor((data) => data.reviewStatus, {
            id: 'reviewStatus',
            header: 'Status',
            cell: ({ getValue }) => <PortfolioReviewStatus reviewStatus={getValue()} />,
        }),
        columnHelper.accessor('portfolioGUID', {
            id: 'liveEdit',
            header: 'Live edit status',
            cell: ({ getValue }) => {
                const clone = portfolios?.find(({ originPortfolioGUID }) => getValue() === originPortfolioGUID);
                return clone ? <PortfolioReviewStatus reviewStatus={clone.reviewStatus} /> : null;
            },
        }),
        columnHelper.accessor((data) => data.name.plainText, {
            id: 'name',
            header: 'Name',
            cell: ({ getValue, row }) => (
                <TextLink to={PortfolioPageRoutePath(globalAccountGUID, row.original.accountGroupGUID)}>
                    {getValue()}
                </TextLink>
            ),
        }),
        columnHelper.accessor('updatedAt', {
            id: 'lastUpdated',
            header: 'Last Updated',
            cell: ({ getValue }) => <Text>{formatDate(getValue())}</Text>,
        }),
        columnHelper.accessor('portfolioGUID', {
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <DropdownMenu>
                    <EllipsisDropdownMenuTrigger />
                    <DropdownMenuContent>
                        <CopyGuidDropdownMenuItem guid={row.original.portfolioGUID} />
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
            size: 50,
        }),
    ];

    return filteredPortfolios.length ? (
        <VerticalLayout $gap={16}>
            <Title section>Outstanding Accounts</Title>
            <Card>
                {portfoliosLoading ? <PageSpinner /> : <CondensedTable data={filteredPortfolios} columns={columns} />}
            </Card>
        </VerticalLayout>
    ) : null;
};
